//------------------------------------------------------ funciones
import {
  subscribeTable,
  createRow,
  updateRow,
  deleteRow,
} from "../supabaseRealtime";
import { supabase } from "../../supabase/SupabaseClient";
import { consultarScania } from "../scania.service";
import { realtime } from "../store";
import { getCargasDeViaje } from "./cargasCombustible.service";
import { autorDe } from "../quien";
import { leerBorrado } from "./decidirBorrado";

const TABLA = "viajes";

const redondear1 = (n) =>
  n === null || n === undefined || Number.isNaN(n)
    ? null
    : Math.round(n * 10) / 10;

const suma = (lista) =>
  lista.reduce((acc, v) => acc + (Number(v) || 0), 0);

// -------------------------------------------------- realtime

// guardamos las filas crudas (sin enriquecer) y el callback activo para
// poder volver a calcular nombreChofer/nombreCliente/dominioFurgon/
// cantidadCargas cuando llegan esas otras tablas DESPUÉS que viajes —
// si no, según el orden en que ganen la carrera las distintas
// suscripciones, esos datos quedaban vacíos hasta el próximo cambio en
// la propia tabla viajes (de ahí que "fluctuaran" entre refrescos).
let ultimasFilasViajes = [];
let callbackViajesActivo = null;

const enriquecerViajes = (rows) => {
  const personasMap = Object.fromEntries(
    (realtime.personas || []).map((p) => [String(p.id), p]),
  );
  const clientesMap = Object.fromEntries(
    (realtime.clientes || []).map((c) => [String(c.id), c]),
  );
  const furgonesMap = Object.fromEntries(
    (realtime.furgones || []).map((f) => [String(f.id), f]),
  );
  // los usuarios de la app se vinculan por mail (no hay un id en común
  // entre auth.users y la tabla `users`, que viene de la migración
  // anterior con ids viejos) -- mismo criterio que usa AuthContext.
  const usuariosPorMail = Object.fromEntries(
    (realtime.users || [])
      .filter((u) => u.mail)
      .map((u) => [String(u.mail).toLowerCase(), u]),
  );
  const cargas = realtime.cargasCombustible || [];

  return rows
    .map((v) => {
      const cargasDelViaje = getCargasDeViaje(cargas, v.id);
      const ultimaCarga = cargasDelViaje[cargasDelViaje.length - 1];
      const internoActual =
        v.estado === "finalizado"
          ? v.internoFinal
          : ultimaCarga?.interno || null;
      return {
        ...v,
        nombreChofer: personasMap[String(v.chofer)]?.nombreCompleto || "",
        nombreCliente: clientesMap[String(v.cliente)]?.nombre || "",
        dominioFurgon: furgonesMap[String(v.furgon)]?.dominio || "",
        internoActual,
        cantidadCargas: cargasDelViaje.length,
        // Quién lo cargó, con el mismo criterio que el resto del hub:
        // `services/quien.js`. Antes esto se resolvía acá adentro, y cualquier
        // otra pantalla que quisiera mostrar el autor tenía que copiarlo.
        autor: autorDe(v, usuariosPorMail),
      };
    })
    .sort((a, b) => b.id.localeCompare(a.id));
};

export const subscribeViajes = (callback) => {
  callbackViajesActivo = callback;
  const unsub = subscribeTable(TABLA, (rows) => {
    ultimasFilasViajes = rows;
    callback(enriquecerViajes(rows));
  });
  return () => {
    callbackViajesActivo = null;
    unsub();
  };
};

// se llama desde store.js cada vez que personas/clientes/furgones/
// cargasCombustible se actualizan, para refrescar los viajes ya
// cargados con los datos nuevos sin esperar a que la tabla viajes
// tenga su propio cambio.
export const reenriquecerViajes = () => {
  if (callbackViajesActivo) callbackViajesActivo(enriquecerViajes(ultimasFilasViajes));
};

// -------------------------------------------------- metodos

const generarIdViaje = async () => {
  const { data: nuevo, error } = await supabase.rpc("incrementar_contador", {
    tabla: "contadores",
    fila_id: "viajes",
  });
  if (error) throw error;
  return `VJ${String(nuevo).padStart(8, "0")}`;
};

export const crearViaje = async ({
  chofer,
  furgon,
  cliente,
  detalle = "",
  fechaInicio,
  usuario = null, // email del usuario logueado que crea el viaje
}) => {
  const id = await generarIdViaje();
  await createRow(TABLA, id, {
    chofer,
    furgon,
    cliente,
    detalle,
    fechaInicio,
    fechaFin: null,
    estado: "en_curso",
    usuario,
  });
  return id;
};

export const updateViaje = (id, data) => updateRow(TABLA, id, data);

export const deleteViaje = (id) => deleteRow(TABLA, id);

/* Borrar un viaje de verdad, y ENTERARSE de si se borró.
 *
 * La decisión de qué significa la respuesta vive en `decidirBorrado`, suelta y
 * sin red: un DELETE bloqueado por la política contesta 204 SIN ERROR, así que
 * `deleteRow` —que sólo mira `error`— diría «listo» con la fila todavía ahí.
 *
 * Acá queda sólo la llamada, y lo único que no se puede sacar de ella es el
 * `.select()`: sin él la respuesta no trae las filas borradas y no hay nada
 * que contar.
 */
export const borrarViaje = async (id) => {
  const r = leerBorrado(
    await supabase.from(TABLA).delete().eq("id", String(id)).select("id"),
  );
  if (!r.ok) throw new Error(r.mensaje);
  return r.cuantas;
};

// -------------------------------------------------- finalizar viaje
//
// Recorta el viaje en tramos a partir de las cargas (cada carga cierra
// un tramo) + el tramo final (entre la ultima carga y el cierre del
// viaje). Por cada tramo consulta SCANIA con el interno y las fechas
// puntuales de ESE tramo (no una sola consulta agregada), porque el
// vehiculo puede haber cambiado a mitad de viaje.

// consulta SCANIA para UN tramo (desde/hasta/interno ya definidos) y
// devuelve el tramo enriquecido. La usan tanto finalizarViaje (primera
// vez) como reintentarScania (cuando algún tramo quedó sin datos).
const consultarUnTramo = async (tramo) => {
  const { data: scania, error } = await consultarScania({
    interno: tramo.interno,
    desde: tramo.desde,
    hasta: tramo.hasta,
  });

  const kmScania = scania?.distance_km ?? null;
  const litrosScania = scania?.fuel_consumed_liters ?? null;
  // mismo dia entero en ambos extremos => SCANIA no puede distinguir
  // el corte por hora (limitacion de la API de FlotaBot, no nuestra)
  const mismoDia =
    scania?.period_start && scania?.period_end &&
    scania.period_start === scania.period_end;

  return {
    ...tramo,
    kmScania,
    litrosScania,
    promedioDeclarado: tramo.kmDeclarado
      ? redondear1((tramo.litrosDeclarado / tramo.kmDeclarado) * 100)
      : null,
    promedioScania: scania?.consumption_l_per_100km ?? null,
    diferenciaKm: kmScania != null ? redondear1(kmScania - tramo.kmDeclarado) : null,
    diferenciaLitros:
      litrosScania != null ? redondear1(litrosScania - tramo.litrosDeclarado) : null,
    dataSource: scania?.data_source ?? null,
    mismoDiaQueOtroTramo: mismoDia,
    error,
  };
};

// recalcula los totales del viaje a partir de la lista de tramos ya
// enriquecidos con SCANIA (se usa tanto al finalizar como al
// reintentar).
const totalesDesdeTramos = (tramosConScania) => {
  const litrosDeclaradoTotal = redondear1(suma(tramosConScania.map((t) => t.litrosDeclarado)));
  const kmDeclaradoTotal = redondear1(suma(tramosConScania.map((t) => t.kmDeclarado)));
  const conScania = tramosConScania.filter((t) => t.kmScania != null);
  const kmScaniaTotal = conScania.length ? redondear1(suma(conScania.map((t) => t.kmScania))) : null;
  const litrosScaniaTotal = conScania.length
    ? redondear1(suma(conScania.map((t) => t.litrosScania)))
    : null;

  return {
    litrosDeclaradoTotal,
    kmDeclaradoTotal,
    kmScaniaTotal,
    litrosScaniaTotal,
    promedioScaniaTotal:
      kmScaniaTotal ? redondear1((litrosScaniaTotal / kmScaniaTotal) * 100) : null,
  };
};

export const finalizarViaje = async ({
  viaje,
  cargas, // ya ordenadas por fecha ascendente
  internoFinal,
  fechaFin,
  kmFinal,
  litrosFinal,
}) => {
  const checkpoints = [
    { fecha: viaje.fechaInicio },
    ...cargas.map((c) => ({
      fecha: c.fecha,
      interno: c.interno,
      litros: c.litros,
      kilometros: c.kilometros,
    })),
    {
      fecha: fechaFin,
      interno: internoFinal,
      litros: Number(litrosFinal) || 0,
      kilometros: Number(kmFinal) || 0,
    },
  ];

  const tramosCrudos = [];
  for (let i = 0; i < checkpoints.length - 1; i++) {
    const cierre = checkpoints[i + 1];
    // si el checkpoint anterior y este tienen la misma fecha exacta
    // (ej: fechaInicio == fecha de la primera carga), el tramo tiene
    // duración cero — no aporta nada y SCANIA no puede consultar un
    // rango vacío, así que lo saltamos en vez de generar un tramo
    // fantasma sin datos.
    if (checkpoints[i].fecha === cierre.fecha) continue;
    tramosCrudos.push({
      desde: checkpoints[i].fecha,
      hasta: cierre.fecha,
      interno: cierre.interno,
      litrosDeclarado: Number(cierre.litros) || 0,
      kmDeclarado: Number(cierre.kilometros) || 0,
    });
  }
  const tramos = tramosCrudos.map((t, i) => ({ tramo: i + 1, ...t }));

  const tramosConScania = [];
  for (const tramo of tramos) {
    tramosConScania.push(await consultarUnTramo(tramo));
  }

  const totales = totalesDesdeTramos(tramosConScania);

  await updateRow(TABLA, viaje.id, {
    estado: "finalizado",
    fechaFin,
    internoFinal,
    kmFinal: Number(kmFinal) || 0,
    litrosFinal: Number(litrosFinal) || 0,
    kmDeclarado: totales.kmDeclaradoTotal,
    litrosDeclaradoTotal: totales.litrosDeclaradoTotal,
    kmScania: totales.kmScaniaTotal,
    litrosScania: totales.litrosScaniaTotal,
    promedioScania: totales.promedioScaniaTotal,
    scaniaConsultadoEn: new Date().toISOString(),
    tramosScania: tramosConScania,
  });

  return tramosConScania;
};

// -------------------------------------------------- reintentar SCANIA
//
// Un viaje finalizado guarda los tramos con lo que SCANIA pudo
// responder EN ESE MOMENTO -- si en esa única pasada algún tramo dio
// error (timeout, FlotaBot caído, etc.), esos datos quedaban perdidos
// para siempre porque no había ninguna forma de volver a consultar.
// Esto reintenta SOLO los tramos sin datos (deja los que ya están
// bien tal cual, para no gastar consultas de más) y actualiza el
// viaje ya finalizado con lo que consiga.
export const reintentarScania = async (viaje) => {
  const tramosExistentes = viaje.tramosScania || [];

  const tramosActualizados = [];
  for (const tramo of tramosExistentes) {
    const yaTieneDatos = tramo.kmScania != null && !tramo.error;
    tramosActualizados.push(yaTieneDatos ? tramo : await consultarUnTramo(tramo));
  }

  const totales = totalesDesdeTramos(tramosActualizados);

  await updateRow(TABLA, viaje.id, {
    kmDeclarado: totales.kmDeclaradoTotal,
    litrosDeclaradoTotal: totales.litrosDeclaradoTotal,
    kmScania: totales.kmScaniaTotal,
    litrosScania: totales.litrosScaniaTotal,
    promedioScania: totales.promedioScaniaTotal,
    scaniaConsultadoEn: new Date().toISOString(),
    tramosScania: tramosActualizados,
  });

  return tramosActualizados;
};
